import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {useMedia, useSlideToggle} from 'coreHooks';
import {getCoords} from 'coreUtils/dom';

import {useTabsCtx} from 'brandComponents/Tabs';
import {useTabCtx} from 'brandComponents/Tabs/Subcomponents/TabPane';

import {getTabsFixedHeight, transformBrandsData} from './helpers';
import {IBrandsProps, TCharGroupData} from './IBrands';
import {BrandsCharacterGroup, BrandsItem, BrandsTitle} from './Subcomponents';

import styles from './Brands.module.scss';

const Brands = ({title, data = [], type = 'alphabetic-scroll'}: IBrandsProps) => {
	const media = useMedia();
	const shouldSlide = media('<=small') && type === 'collapsible';
	const {togglerRef, handleSlideToggle} = useSlideToggle<HTMLDivElement>(shouldSlide);
	const [isOpen, setIsOpen] = useState(false);
	const tabsCtx = useTabsCtx(true);
	const tabCtx = useTabCtx(true);
	const transformedData = useMemo<TCharGroupData>(() => transformBrandsData(data), [data]);
	const characters = useMemo(() => Array.from(transformedData.keys()), [transformedData]);
	const charsGroupRefArray = useRef([]);
	const lastActiveCharGroup = useRef('');
	const isAlphabeticScroll = type === 'alphabetic-scroll';
	const [triggerCharGroupRef, setTriggerCharGroupRef] = useState(Date.now());

	const handleCharGroupRef = useCallback((charGroupRef) => {
		const coordinates = getCoords(charGroupRef);
		const character = charGroupRef.getAttribute('data-character');

		if (
			coordinates.top === 0 ||
			charsGroupRefArray.current.find((item) => item.char === character)
		) {
			return;
		}

		const data = {
			char: character,
			...coordinates,
			bottom: parseInt(window.getComputedStyle(charGroupRef).height, 10) + coordinates.top,
		};

		charsGroupRefArray.current.push(data);
	}, []);

	const handleListOpen = useCallback(() => {
		if (ViewportMgr.query('>small')) {
			return;
		}

		setIsOpen((prevState) => {
			handleSlideToggle(!prevState);

			return !prevState;
		});
	}, [tabsCtx?.activeTabId]);

	const onScrollCallback = useCallback(() => {
		const pageYOffset = window.pageYOffset;

		if (window.pageYOffset === 0) {
			tabsCtx?.setActiveBrandCharGroup(characters[0]);
			lastActiveCharGroup.current = characters[0];
		} else {
			const header = ViewportMgr.getHeaderHeightTotal();
			const tabsHeight = getTabsFixedHeight(ViewportMgr.query('>small'));
			const dynamicOffset = header + tabsHeight;
			const activeItem = charsGroupRefArray.current.find((item) => {
				return pageYOffset > (item.top - dynamicOffset) && pageYOffset < (item.bottom - dynamicOffset);
			});

			if (activeItem && lastActiveCharGroup.current !== activeItem.char) {
				tabsCtx?.setActiveBrandCharGroup(activeItem.char);
				lastActiveCharGroup.current = activeItem.char;
			}
		}
	}, []);

	useEffect(() => {
		if (!isAlphabeticScroll) {
			return () => null;
		}

		if (tabsCtx?.activeTabId) {
			charsGroupRefArray.current = [];
			setTriggerCharGroupRef(Date.now());
		}
	}, [tabsCtx?.activeTabId]);

	// Lift up side component data, used for side component in tabs
	useEffect(() => {
		const isInTabsCtx = !!tabsCtx && !!tabCtx;

		if (characters.length && isInTabsCtx) {
			tabsCtx.registerSideComponentData(tabCtx.tabId, characters, type);
		}
	}, []);

	useEffect(() => {
		if (!isAlphabeticScroll) {
			return () => null;
		}

		window.addEventListener('scroll', onScrollCallback);

		return () => {
			window.removeEventListener('scroll', onScrollCallback);
		};
	}, []);

	return (
		<div
			className={cn(styles['brands'], type || 'alphabetic-scroll', {
				[styles['brands--active']]: isOpen,
			})}
		>
			<BrandsTitle title={title} handleListOpen={handleListOpen} />

			<div className={styles['brands__content']} ref={togglerRef}>
				<div className={styles['brands__content__inner']}>
					{characters.map((character) => (
						<BrandsCharacterGroup
							key={`brands_${character}`}
							handleCharGroupRef={handleCharGroupRef}
							type={type}
							character={character}
							triggerCharGroupRef={triggerCharGroupRef}
						>
							{Array.from(transformedData.get(character)).map((brandItem) => (
								<BrandsItem
									url={brandItem.url}
									key={`${brandItem.id}_${brandItem.label}_${brandItem.url}`}
								>
									{brandItem.label}
								</BrandsItem>
							))}
						</BrandsCharacterGroup>
					))}
				</div>
			</div>
		</div>
	);
};

export default Brands;
